import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  useGetParticipantTransactionsPaymentAccountCardTransactionsGet,
  type TransactionResponse,
} from "@/api-v3";
import { ErrorCard } from "@/components/error-card";
import { Skeleton } from "@/components/ui/skeleton";
import { ManageCard } from "@/features/card/virtual-card";
import { usePaymentMethods } from "@/features/payments";
import { type PaymentAccountCardType } from "@/features/payments/types/payment-account-cards";
import { formatCurrency } from "@/lib/currency-formatter";
import { formatISODate } from "@/lib/date-formatter";
import { parseError } from "@/lib/error-handler";
import { cn } from "@/lib/utils";

import {
  filterTransactionsForCard,
  getAmount,
  getTransactionTitleByType,
  groupAndSortByDate,
  isNoMoneyMoved,
} from "./helpers";

export type Transaction = TransactionResponse;

export const CardTransactionsPage = () => {
  const { t } = useTranslation();

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useGetParticipantTransactionsPaymentAccountCardTransactionsGet({
    query: {
      staleTime: 30000,
      refetchOnWindowFocus: true,
    },
  });
  const { cardType } = useParams<{ cardType: PaymentAccountCardType }>();
  const transactions: TransactionResponse[] = filterTransactionsForCard(
    response ?? [],
    cardType
  );

  const { cards } = usePaymentMethods();

  const cardDetails = cards.find((card) => card.type === cardType) ?? null;

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="space-y-1.5">
          <Skeleton
            role="status"
            aria-label="Loading card transaction"
            className="h-14"
          />
          <Skeleton
            role="status"
            aria-label="Loading card transaction"
            className="h-14"
          />
          <Skeleton
            role="status"
            aria-label="Loading card transaction"
            className="h-14"
          />
        </div>
      </div>
    );
  }

  if (error) {
    const { title, message } = parseError(error);

    return (
      <ErrorCard
        title={t(title)}
        message={t(message)}
        buttonLabel={t("retry")}
        onButtonClick={refetch}
      />
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="w-full">
        {cardDetails && (
          <div className="mb-8">
            <ManageCard hideManageButton card={cardDetails} />
          </div>
        )}
        <div className="bg-background rounded-lg p-4 flex items-center justify-center h-20">
          <p className="text-center text-sm text-muted-foreground">
            {t("no_transactions_found")}
          </p>
        </div>
      </div>
    );
  }

  const groupedTransactions = groupAndSortByDate(transactions, "created_at");

  return (
    <div className="w-full pb-12">
      {cardDetails && (
        <div className="mb-8">
          <ManageCard hideManageButton card={cardDetails} />
        </div>
      )}
      <div className="space-y-6">
        {Object.keys(groupedTransactions).map((date) => (
          <Fragment key={date}>
            <div className="flex justify-between">
              <p className="line-height-loose font-semibold text-gray-600">
                {formatISODate(date)}
              </p>
            </div>
            <div className="space-y-1.5" role="list">
              {groupedTransactions[date].map((transaction) => {
                const title = getTransactionTitleByType(transaction);
                const amount = getAmount(transaction);
                const noMoneyMoved = isNoMoneyMoved(transaction);
                const details =
                  transaction.description ??
                  transaction.message ??
                  (transaction.type === "pending" ? t("pending") : null);
                return (
                  <div
                    role="listitem"
                    className="rounded-lg border bg-card text-card-foreground shadow-xs"
                    key={String(transaction.id)}
                    aria-label="transaction"
                  >
                    <div className="p-4">
                      <div className="flex justify-between gap-4">
                        <div>
                          <p>
                            {transaction.merchant_name
                              ? transaction.merchant_name
                              : t(title)}
                          </p>
                          <p className="text-muted-foreground text-sm">
                            {details}
                          </p>
                        </div>
                        <div className="shrink-0">
                          <p
                            aria-label="amount"
                            className={cn(
                              "text-right text-lg font-semibold font-mono",
                              noMoneyMoved
                                ? "text-muted-foreground line-through"
                                : Number(amount) > 0
                                  ? "text-green-600"
                                  : "text-red-600"
                            )}
                          >
                            {formatCurrency(amount, transaction.currency)}
                          </p>
                          {transaction.merchant_currency &&
                            transaction.merchant_amount != null && (
                              <p
                                aria-label="merchant amount"
                                className="text-right text-sm text-muted-foreground font-mono"
                              >
                                {formatCurrency(
                                  transaction.merchant_amount,
                                  transaction.merchant_currency
                                )}
                              </p>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
};
